import { useId } from 'react';
import clsx from 'clsx';
import type { ColumnView, RetroSettings } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';

/**
 * A switch, wired up for screen readers.
 *
 * Note the explicit `aria-labelledby`: a `<label>` element does not label a
 * `<button>`, so wrapping one would leave the switch with no accessible name at
 * all. The hint is attached with `aria-describedby` rather than folded into the
 * name, so it is available but not read out as part of the label.
 */
function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  const id = useId();

  return (
    <div className={clsx('flex items-start gap-3 py-2', disabled && 'opacity-50')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={hint ? `${id}-hint` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          'mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition',
          checked ? 'bg-brand-600' : 'bg-black/20',
        )}
      >
        <span
          className={clsx(
            'h-4 w-4 rounded-full bg-white shadow transition',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
      <span className="min-w-0">
        {/* A redundant click target for convenience. Deliberately not focusable
            and given no role: the switch above is the real control. */}
        <span
          id={`${id}-label`}
          onClick={() => !disabled && onChange(!checked)}
          className="block cursor-pointer text-sm"
        >
          {label}
        </span>
        {hint ? (
          <span id={`${id}-hint`} className="block text-xs text-ink/55">
            {hint}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function Stepper({
  value,
  onChange,
  min,
  max,
  label,
}: {
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  label: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm">{label}</span>
      <span className="flex items-center gap-1">
        <button
          type="button"
          className="btn h-7 w-7 !px-0"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          aria-label="Decrease"
        >
          −
        </button>
        <span className="w-8 text-center text-sm font-medium tabular-nums">{value}</span>
        <button
          type="button"
          className="btn h-7 w-7 !px-0"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label="Increase"
        >
          +
        </button>
      </span>
    </div>
  );
}

function Section({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return (
    <section className="border-t border-black/5 py-3 first:border-t-0">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink/45">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Facilitation settings. Mirrors the three groups of controls a facilitator
 * needs mid-session; each change is broadcast immediately so participants see
 * the board rules change under them without a reload.
 */
export function SettingsPanel({
  settings,
  columns,
  open,
  onClose,
}: {
  settings: RetroSettings;
  columns: ColumnView[];
  open: boolean;
  onClose: () => void;
}) {
  const send = useRetroStore((state) => state.send);

  const update = (patch: {
    comments?: Partial<RetroSettings['comments']>;
    group?: Partial<RetroSettings['group']>;
    vote?: Partial<RetroSettings['vote']>;
  }) => void send('retro:updateSettings', { settings: patch });

  const votable = settings.vote.votableColumnIds;

  return (
    <>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-ink/30 lg:hidden"
          aria-label="Close settings"
          onClick={onClose}
        />
      ) : null}

      <aside
        aria-label="Facilitation settings"
        className={clsx(
          'fixed right-0 top-0 z-40 flex h-dvh w-full max-w-sm flex-col bg-white shadow-panel transition-transform',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
        aria-hidden={!open}
      >
        <header className="flex items-center justify-between border-b border-black/5 px-4 py-3">
          <h2 className="font-semibold">Facilitation settings</h2>
          <button
            type="button"
            className="rounded-full p-1.5 text-ink/60 hover:bg-black/5"
            onClick={onClose}
            aria-label="Close settings"
          >
            ✕
          </button>
        </header>

        <div className="thin-scroll flex-1 overflow-y-auto px-4 py-2">
          <Section title="Comments">
            {/* allowGifs stays in the shared schema, but the toggle is hidden
                until the comment composer can actually send GIFs — offering a
                switch for a feature that does not exist is a lie. */}
            <Toggle
              label="Limit comments per participant"
              hint="Encourages people to pick their most important points."
              checked={settings.comments.maxCommentsPerParticipant !== null}
              onChange={(limited) =>
                update({ comments: { maxCommentsPerParticipant: limited ? 5 : null } })
              }
            />
            {settings.comments.maxCommentsPerParticipant !== null ? (
              <Stepper
                label="Maximum comments each"
                min={1}
                max={50}
                value={settings.comments.maxCommentsPerParticipant}
                onChange={(maxCommentsPerParticipant) => update({ comments: { maxCommentsPerParticipant } })}
              />
            ) : null}
          </Section>

          <Section title="Group">
            <Toggle
              label="Restrict grouping to facilitators"
              hint="On by default. Turn it off to let everyone group cards and tag topics."
              checked={settings.group.restrictGroupingToFacilitators}
              onChange={(restrictGroupingToFacilitators) =>
                update({ group: { restrictGroupingToFacilitators } })
              }
            />
            <Toggle
              label="Restrict filtering to facilitators"
              checked={settings.group.restrictFilteringToFacilitators}
              onChange={(restrictFilteringToFacilitators) =>
                update({ group: { restrictFilteringToFacilitators } })
              }
            />
            <Toggle
              label="Restrict sorting to facilitators"
              checked={settings.group.restrictSortingToFacilitators}
              onChange={(restrictSortingToFacilitators) =>
                update({ group: { restrictSortingToFacilitators } })
              }
            />
            <Toggle
              label="Restrict highlighting to facilitators"
              checked={settings.group.restrictHighlightingToFacilitators}
              onChange={(restrictHighlightingToFacilitators) =>
                update({ group: { restrictHighlightingToFacilitators } })
              }
            />
            <Toggle
              label="Allow new comments after the reveal"
              checked={settings.group.allowNewComments}
              onChange={(allowNewComments) => update({ group: { allowNewComments } })}
            />
            <Toggle
              label="Allow emoji reactions"
              checked={settings.group.allowReactions}
              onChange={(allowReactions) => update({ group: { allowReactions } })}
            />
            <Toggle
              label="Suggest groups automatically"
              hint="Needs an AI provider configured on the server."
              checked={settings.group.aiSuggestions}
              onChange={(aiSuggestions) => update({ group: { aiSuggestions } })}
            />
            {settings.group.aiSuggestions ? (
              <div className="flex gap-1 pb-2 pl-12">
                {(
                  [
                    ['SAME_COLUMN', 'Within a column'],
                    ['ACROSS_COLUMNS', 'Across columns'],
                  ] as const
                ).map(([scope, label]) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => update({ group: { aiScope: scope } })}
                    className={clsx(
                      'rounded-full px-2.5 py-1 text-xs font-medium',
                      settings.group.aiScope === scope ? 'bg-ink text-white' : 'bg-black/5 text-ink/70',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </Section>

          <Section title="Vote">
            <Stepper
              label="Votes per participant"
              min={1}
              max={20}
              value={settings.vote.votesPerParticipant}
              onChange={(votesPerParticipant) => update({ vote: { votesPerParticipant } })}
            />
            <Toggle
              label="Limit votes per card"
              hint="Stops one person spending everything on a single item."
              checked={settings.vote.maxVotesPerTarget !== null}
              onChange={(limited) => update({ vote: { maxVotesPerTarget: limited ? 1 : null } })}
            />
            {settings.vote.maxVotesPerTarget !== null ? (
              <Stepper
                label="Maximum votes per card"
                min={1}
                max={20}
                value={settings.vote.maxVotesPerTarget}
                onChange={(maxVotesPerTarget) => update({ vote: { maxVotesPerTarget } })}
              />
            ) : null}
            <Toggle
              label="Only allow voting in some columns"
              checked={votable !== null}
              onChange={(restricted) =>
                update({ vote: { votableColumnIds: restricted ? columns.map((c) => c.id) : null } })
              }
            />
            {votable !== null ? (
              <ul className="space-y-1 pb-2 pl-12">
                {columns.map((column) => {
                  const enabled = votable.includes(column.id);
                  return (
                    <li key={column.id}>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() =>
                            update({
                              vote: {
                                votableColumnIds: enabled
                                  ? votable.filter((id) => id !== column.id)
                                  : [...votable, column.id],
                              },
                            })
                          }
                        />
                        <span aria-hidden>{column.emoji}</span>
                        {column.title}
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {votable !== null && votable.length === 0 ? (
              <p className="pb-2 pl-12 text-xs text-amber-700">
                No columns are votable — nobody will be able to vote.
              </p>
            ) : null}
          </Section>
        </div>
      </aside>
    </>
  );
}
